import { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  Box,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  Button,
} from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import SaveIcon from '@material-ui/icons/Save';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  chip: {
    cursor: 'pointer',
  },
  chipSelected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  chipUnselected: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
    opacity: 0.6,
    transition: 'opacity 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      opacity: 1,
      backgroundColor: theme.palette.action.hover,
    },
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  error: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
  },
  empty: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontStyle: 'italic',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  saveButton: {
    fontSize: '0.75rem',
    padding: '2px 8px',
    minWidth: 'auto',
  },
}));

export interface GithubTeam {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  organization: {
    login: string;
  };
}

export interface GithubTeamsBarProps {
  /** Maximum number of teams to show (default: unlimited) */
  maxTeams?: number;
  /** Controlled selection state - array of selected team slugs */
  selectedTeams?: string[];
  /** Whether the user chip is selected (controlled) */
  userSelected?: boolean;
  /** Callback when selection changes */
  onSelectionChange?: (selectedTeams: string[]) => void;
  /** Callback when user selection changes */
  onUserSelectionChange?: (selected: boolean) => void;
  /** Callback when teams are loaded */
  onTeamsLoaded?: (teams: GithubTeam[]) => void;
  /** Callback when username is loaded */
  onUsernameLoaded?: (username: string) => void;
  /** Whether edit mode is active (shows Save as Default button) */
  editMode?: boolean;
  /** Callback when Save as Default is clicked */
  onSaveDefault?: () => void;
}

export const GithubTeamsBar = ({
  maxTeams,
  selectedTeams,
  userSelected,
  onSelectionChange,
  onUserSelectionChange,
  onTeamsLoaded,
  onUsernameLoaded,
  editMode = false,
  onSaveDefault,
}: GithubTeamsBarProps) => {
  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);

  const [teams, setTeams] = useState<GithubTeam[]>([]);
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Internal selection state (used when uncontrolled)
  const [internalSelected, setInternalSelected] = useState<Set<string>>(new Set());
  const [internalUserSelected, setInternalUserSelected] = useState(true);

  // Determine if we're in controlled mode
  const isControlled = selectedTeams !== undefined;
  const isUserControlled = userSelected !== undefined;
  const effectiveSelected = isControlled
    ? new Set(selectedTeams)
    : internalSelected;
  const effectiveUserSelected = isUserControlled ? userSelected : internalUserSelected;

  const fetchUserTeams = useCallback(async (retryCount = 0) => {
    setLoading(true);
    setError(null);

    try {
      // Force a fresh token on retry
      const token = await githubAuth.getAccessToken(['read:org'], { optional: false, instantPopup: false });

      // Fetch the authenticated user's info to get username
      const userResponse = await fetch('https://api.github.com/user', {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.github.v3+json',
        },
      });

      if (!userResponse.ok) {
        const responseText = await userResponse.text();
        // Handle "Bad credentials" by retrying once with fresh token
        if (userResponse.status === 401 && retryCount < 1) {
          console.warn('GitHub token may be stale, retrying...');
          // Small delay before retry
          await new Promise(resolve => setTimeout(resolve, 500));
          return fetchUserTeams(retryCount + 1);
        }
        throw new Error(responseText.includes('Bad credentials') ? 'Not authenticated with GitHub' : `GitHub API error: ${userResponse.statusText}`);
      }

      const userData = await userResponse.json();
      setUsername(userData.login);
      onUsernameLoaded?.(userData.login);

      // Fetch all teams the user belongs to (paginated)
      let allTeams: GithubTeam[] = [];
      let page = 1;
      const perPage = 100;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `https://api.github.com/user/teams?per_page=${perPage}&page=${page}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/vnd.github.v3+json',
            },
          },
        );

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Not authenticated with GitHub');
          }
          throw new Error(`GitHub API error: ${response.statusText}`);
        }

        const data: GithubTeam[] = await response.json();
        allTeams = allTeams.concat(data);

        hasMore = data.length === perPage;
        page++;
      }

      // Sort by team name
      allTeams.sort((a, b) => a.name.localeCompare(b.name));

      setTeams(allTeams);

      // Initialize internal selection to all teams if uncontrolled
      if (!isControlled) {
        setInternalSelected(new Set(allTeams.map(t => t.slug)));
      }

      // Notify parent of loaded teams
      onTeamsLoaded?.(allTeams);
    } catch (err) {
      console.error('Failed to fetch user teams:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  }, [githubAuth, isControlled, onTeamsLoaded, onUsernameLoaded]);

  useEffect(() => {
    fetchUserTeams();
  }, [fetchUserTeams]);

  const getTeamReposUrl = (team: GithubTeam): string => {
    return `https://github.com/orgs/${team.organization.login}/teams/${team.slug}/repositories`;
  };

  const handleChipClick = (team: GithubTeam, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const newSelected = new Set(effectiveSelected);
    if (newSelected.has(team.slug)) {
      newSelected.delete(team.slug);
    } else {
      newSelected.add(team.slug);
    }

    if (isControlled) {
      onSelectionChange?.(Array.from(newSelected));
    } else {
      setInternalSelected(newSelected);
    }
  };

  const handleChipLinkClick = (team: GithubTeam, event: React.MouseEvent) => {
    // Middle click or ctrl/cmd+click opens the link
    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      window.open(getTeamReposUrl(team), '_blank', 'noopener,noreferrer');
    }
  };

  const handleUserChipClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const newUserSelected = !effectiveUserSelected;
    if (isUserControlled) {
      onUserSelectionChange?.(newUserSelected);
    } else {
      setInternalUserSelected(newUserSelected);
    }
  };

  const handleUserChipLinkClick = (event: React.MouseEvent) => {
    // Middle click or ctrl/cmd+click opens the user's GitHub profile
    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      window.open(`https://github.com/${username}`, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.label}>
          <GroupIcon fontSize="small" />
          Your Teams
        </Typography>
        <Box className={classes.loading}>
          <CircularProgress size={16} />
          <span>Loading teams...</span>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.label}>
          <GroupIcon fontSize="small" />
          Your Teams
        </Typography>
        <Typography className={classes.error}>{error}</Typography>
      </Box>
    );
  }

  if (teams.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography className={classes.label}>
          <GroupIcon fontSize="small" />
          Your Teams
        </Typography>
        <Typography className={classes.empty}>
          No GitHub teams found
        </Typography>
      </Box>
    );
  }

  const displayTeams = maxTeams ? teams.slice(0, maxTeams) : teams;
  const hasMore = maxTeams && teams.length > maxTeams;
  const totalItems = teams.length + (username ? 1 : 0);
  const selectedCount = effectiveSelected.size + (effectiveUserSelected ? 1 : 0);

  return (
    <Box className={classes.container}>
      <Box className={classes.headerRow}>
        <Typography className={classes.label}>
          <GroupIcon fontSize="small" />
          Filter by ({selectedCount}/{totalItems} selected)
        </Typography>
        {editMode && onSaveDefault && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<SaveIcon style={{ fontSize: 14 }} />}
            onClick={onSaveDefault}
            className={classes.saveButton}
          >
            Save as Default
          </Button>
        )}
      </Box>
      <Box className={classes.chipsContainer}>
        {/* User chip - always first */}
        {username && (
          <Tooltip
            title={effectiveUserSelected
              ? `Click to hide your personal repos. Ctrl+click to open GitHub profile.`
              : `Click to show your personal repos. Ctrl+click to open GitHub profile.`}
            arrow
          >
            <Chip
              icon={<PersonIcon style={{ fontSize: 16 }} />}
              label={username}
              size="small"
              className={`${classes.chip} ${effectiveUserSelected ? classes.chipSelected : classes.chipUnselected}`}
              onClick={handleUserChipClick}
              onMouseDown={handleUserChipLinkClick}
            />
          </Tooltip>
        )}
        {displayTeams.map(team => {
          const isSelected = effectiveSelected.has(team.slug);
          const tooltipText = isSelected
            ? `Click to hide ${team.name} content. Ctrl+click to open repos.`
            : `Click to show ${team.name} content. Ctrl+click to open repos.`;

          return (
            <Tooltip
              key={team.id}
              title={team.description || tooltipText}
              arrow
            >
              <Chip
                label={team.name}
                size="small"
                className={`${classes.chip} ${isSelected ? classes.chipSelected : classes.chipUnselected}`}
                onClick={(e) => handleChipClick(team, e)}
                onMouseDown={(e) => handleChipLinkClick(team, e)}
              />
            </Tooltip>
          );
        })}
        {hasMore && (
          <Chip
            label={`+${teams.length - maxTeams} more`}
            size="small"
            variant="outlined"
            disabled
          />
        )}
      </Box>
    </Box>
  );
};
